import { Link } from "react-router-dom";
import { ArrowLeft, ScrollText } from "lucide-react";

// Version string mirrors backend TERMS_VERSION — bump both when the text materially changes.
export const TERMS_VERSION = "v6-2026-09";

export default function Voorwaarden() {
  return (
    <div data-testid="voorwaarden-page" className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
      <Link
        to="/events"
        className="inline-flex items-center gap-2 text-slate-300 hover:text-white text-sm mb-6"
        data-testid="voorwaarden-back"
      >
        <ArrowLeft className="h-4 w-4" /> Terug
      </Link>

      <div className="text-xs uppercase tracking-widest text-svj-cyan font-semibold flex items-center gap-2">
        <ScrollText className="h-3.5 w-3.5" /> Versie {TERMS_VERSION}
      </div>
      <h1 className="mt-2 text-4xl sm:text-5xl font-extrabold text-white tracking-tight">
        Algemene voorwaarden
      </h1>
      <p className="mt-3 text-slate-300">
        Deze voorwaarden gelden voor elke ticketaankoop via het StudioVoorJou ticket-platform.
      </p>

      <div className="mt-10 space-y-8 text-slate-300 leading-relaxed">
        <section>
          <h2 className="text-lg font-bold text-white">1. Wie zijn wij</h2>
          <p className="mt-2">
            StudioVoorJou biedt dit platform aan namens de organisator die het event verkoopt.
            De organisator is verantwoordelijk voor de inhoud van het event; StudioVoorJou zorgt
            voor de verkoop, betaling en levering van de tickets.
          </p>
        </section>

        <section>
          <h2 className="text-lg font-bold text-white">2. Bestellen &amp; betalen</h2>
          <ul className="mt-2 list-disc pl-5 space-y-1.5">
            <li>Bestellingen komen pas tot stand na een geslaagde betaling via onze betaalprovider.</li>
            <li>Prijzen zijn inclusief btw. De btw-splitsing staat op je bevestiging.</li>
            <li>Na betaling ontvang je direct je tickets per e-mail met een unieke QR-code.</li>
          </ul>
          <div className="mt-3 rounded-lg border border-svj-border/60 bg-svj-bg/40 p-3">
            <p className="text-xs font-semibold uppercase tracking-widest text-slate-400">
              Aanvullende voorwaarde per event
            </p>
            <ul className="mt-2 list-disc pl-5 space-y-1.5 text-slate-400 text-sm">
              <li>
                Voor een specifiek event kan de organisator, in overleg met StudioVoorJou, een
                aanvullende of afwijkende voorwaarde hebben (bijvoorbeeld een ander
                terugbetalingsbeleid).
              </li>
              <li>
                Die tekst staat dan duidelijk gelabeld op de afrekenpagina van dat event, met een
                eigen akkoordvinkje — nooit verborgen in deze algemene voorwaarden.
              </li>
              <li>
                Bij tegenstrijdigheid geldt: die aanvullende voorwaarde gaat voor op déze algemene
                voorwaarden, maar uitsluitend op het punt waarop hij afwijkt en uitsluitend voor dat
                ene evenement; voor al het overige — en voor elk ander event — blijven deze
                algemene voorwaarden onverkort van toepassing.
              </li>
              <li>
                Dit geldt nooit voor wettelijke regels zoals het herroepingsrecht hieronder (artikel
                3) — die volgen uit de wet, niet uit een afspraak tussen StudioVoorJou en een
                organisator, en kunnen dus door geen enkele aanvullende voorwaarde worden gewijzigd.
              </li>
            </ul>
          </div>
        </section>

        <section>
          <h2 className="text-lg font-bold text-white">3. Herroepingsrecht</h2>
          <p className="mt-2">
            Voor tickets voor vrijetijdsbesteding op een specifieke datum geldt het wettelijke
            herroepingsrecht <b>niet</b> (artikel 6:230p sub e BW). Je bestelling is dus definitief
            zodra je hebt betaald.
          </p>
        </section>

        <section>
          <h2 className="text-lg font-bold text-white">4. Annulering door de organisator</h2>
          <p className="mt-2">
            Gaat een event niet door of wordt het significant gewijzigd? Dan ontvang je binnen 14
            dagen automatisch het volledige aankoopbedrag retour op dezelfde rekening waarmee je
            betaald hebt. Je krijgt hierover per e-mail bericht.
          </p>
        </section>

        <section>
          <h2 className="text-lg font-bold text-white">5. Persoonlijke gegevens</h2>
          <p className="mt-2">
            We gebruiken je gegevens alleen om je tickets te versturen en om de organisator een
            deelnemerslijst te geven. Betaalgegevens worden niet door StudioVoorJou opgeslagen —
            die gaan direct naar onze betaalprovider. Lees ons volledige{" "}
            <Link to="/privacybeleid" className="text-svj-cyan hover:underline">
              privacybeleid
            </Link>{" "}
            voor meer details over welke gegevens we verwerken, met wie we ze delen en hoe lang
            we ze bewaren.
          </p>
        </section>

        <section>
          <h2 className="text-lg font-bold text-white">6. Doorverkoop &amp; misbruik</h2>
          <p className="mt-2">
            Doorverkoop boven de nominale prijs is niet toegestaan. Elke QR-code kan één keer
            gescand worden — kopieën of screenshots die eerder worden aangeboden, geven geen
            toegang.
          </p>
        </section>

        <section>
          <h2 className="text-lg font-bold text-white">7. Aansprakelijkheid</h2>
          <p className="mt-2">
            StudioVoorJou is niet aansprakelijk voor schade tijdens het event zelf. De organisator
            is verantwoordelijk voor veiligheid, programma en toegang op locatie.
          </p>
        </section>

        <section>
          <h2 className="text-lg font-bold text-white">8. Contact</h2>
          <p className="mt-2">
            Vragen? Mail{" "}
            <a href="mailto:info@studiovoorjou.nl" className="text-svj-cyan hover:underline">
              info@studiovoorjou.nl
            </a>{" "}
            en we helpen je zo snel mogelijk.
          </p>
        </section>
      </div>

      <div className="mt-14 text-xs text-slate-500">
        Laatst bijgewerkt: {new Date().toLocaleDateString("nl-NL", { month: "long", year: "numeric" })}
      </div>
    </div>
  );
}
